/* eslint-disable react/no-unused-state */
// © 2015-2021 WDNA - All Rights Reserved

import _ from 'lodash';
import * as React from 'react';

import { UserDO } from 'wdna-components/lib/types.new';
import { DashComponent, DashboardLauncher } from 'wdna-components/lib/dashboards';
import { messageError } from 'wdna-components/lib/helpers/notification';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

/* Types */
import { FilterContext } from '../../../../types';

/* Sidebar */
import MainFilter from '../components/addons/MainFilter';

import { waste } from '../../../sensors/requests';

/* Components */
import MainTable from '../components/drilldown/MainTable';

const dashConfig: DashComponent[] = [
  {
    widths: [12, 12, 12],
    component: MainTable,
    props: {
      title: 'Drilldown Table',
      loading: false,
    },
  },
];

interface Props {
  type: string;
  mainFilter: any;
}

interface State {
  mainFilter?: any;
  sidebar?: React.ReactNode[];
  dashboardData: any[];
}

class MainDashboard extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private userIsURW: boolean;

  public constructor(props: Props) {
    super(props);

    const { mainFilter } = props;

    this.pid = window.getStr();
    this.mounted = false;
    this.userIsURW = false;

    this.state = {
      mainFilter,
      sidebar: undefined,
      dashboardData: [],
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.userIsURW = hasLevel('role', ['ADM', 'URW']);

    this.fetchData();
  }

  public componentDidUpdate(prevProps: Props): void {
    if (this.props !== prevProps) {
      this.fetchData();
    }
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public updateParent = (data: State, cb?: (() => void)): void => {
    this.setLocalState({ ...data }, () => {
      if (typeof cb === 'function') cb();
    });
  };

  public fetchData = (): void => {
    window.store.dispatch(initProcess(this.pid));
    try {
      this.processSidebarConfig();
    } catch (error: any) {
      messageError('Error', error.message);
    }

    setTimeout(() => {
      window.store.dispatch(finishProcess(this.pid));
    }, 1000);
  };

  public processSidebarConfig = (): void => {
    const { mainFilter } = this.props;
    const sidebar: React.ReactNode[] = [(): JSX.Element => <MainFilter initialState={mainFilter} requestFunc={this.getDashboardData} />];
    this.setLocalState({ sidebar });
  };

  public getDashboardData = async (): Promise<any> => {
    const { type } = this.props;
    const filter: any = this.state.mainFilter;

    const req: Record<string, any> = {};
    if (type !== 'ALL') req.type = type;
    if (!_.isEmpty(filter)) {
      const { date1, date2 } = filter.arrFilterDate[0];
      req.from = date1;
      req.to = date2;
    }
    if (!this.userIsURW) {
      const user: UserDO = _.get(window.store.getState(), ['auth', 'user']);

      req.verified = true;
      if (_.isFinite(user.access?.companyId)) {
        req.companyId = user.access.companyId;
      }
    } else {
      if (_.toNumber(filter.verified) === 1) {
        req.verified = true;
      }
      req.companyId = filter.company;
    }

    return waste('unload_list', undefined, req);
  };

  public render(): React.ReactNode {
    const { sidebar } = this.state;
    if (!dashConfig || !sidebar) return null;
    return (
      <FilterContext.Provider value={{ parentState: this.state, updateParent: this.updateParent }}>
        <DashboardLauncher config={dashConfig} sidebar={sidebar} hiddenSidebar />
      </FilterContext.Provider>
    );
  }
}

const View: React.MemoExoticComponent<any> = React.memo((props: Props): JSX.Element => {
  return <MainDashboard {...props} />;
});

export default View;
